/**
 * @module ProductApiService
 * @description
 * Angular service providing HTTP API access for all product-related operations.
 * Covers products, product groups, price variables, stock levels, vendors,
 * parts, customs codes, booking, turnover sheets, and reporting endpoints.
 *
 * All requests are authenticated via custom Webyto headers resolved from
 * {@link GetglobalsettingsService}.
 */
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { of, Observable, throwError } from 'rxjs';
import { retry, catchError, map } from 'rxjs/operators';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { ProductGroup } from 'src/app/shared/product-group';
import { PriceVariable } from 'src/app/shared/price-variable';
import {
  Product,
  ProductListFetchParams,
  ProductInfo,
  ProdPrice,
  ProductParts,
  ProdVendors,
  ProductStat,
  ProductBooking,
  TurnoverRow,
  ApprovedCustomsCodes,
  ProductBatchInfo,
  ProductSerialHistory
} from 'src/app/shared/product';

/**
 * @interface ProductTransaction
 * @description
 * Sales Invoice Line Item Interface.
 * Represents individual product lines in sales documents with customer and pricing information.
 */
interface ProductTransaction {
  /** Product code/SKU */
  PRODCODE: string;
  /** Internal product ID */
  PRODID: number;
  /** Product name/description */
  DOCITEMPRODNAME: string;

  /** Item price in local currency */
  DOCITEMPRICE: number;
  /** Item price in foreign currency (e.g. USD/EUR) */
  DOCITEMVPRICE: number;

  /** Quantity ordered */
  ORDERED: number;
  /** Quantity shipped */
  SHIPPED: number;

  /** Product group name (Russian) */
  PGROUPNAME: string;
  /** Product category (Ukrainian) */
  ProductCategory: string;

  /** Invoice number/document number */
  INVOICE: string;
  /** Internal invoice ID */
  INVOICEID: number;
  /** Document date (ISO format) */
  DOCDATE: Date | string;
  /** Document item ID */
  DOCITEMID: number;

  /** Customer short name/nickname */
  CUSTOMER_NICKNAME: string;
  /** Full customer legal name */
  CUSTOMER: string;
  /** Internal customer ID */
  CUSTOMERID: number;

  /** Seller/vendor name */
  SELLER: string;
  /** Document author/creator */
  AUTHOR: string;
  /** Document management client ID */
  DOCMANCLIID: number;
}

/**
 * @interface StockResult
 * @description
 * Shared interface for all stock-level endpoint responses.
 *
 * Replaces the five previous ad-hoc single-field interfaces:
 * - `{ PRODSTOCK: number }` – GET /Product/:id/Stock/…
 * - `{ STOCKBE:   number }` – GET /Product/:id/StockBefore/…
 * - `{ STOCKAF:   number }` – GET /Product/:id/StockAfter/…
 * - `{ LCSTOCKBE: number }` – GET /Product/:id/LocalStockBefore/…
 * - `{ LCSTOCKAF: number }` – GET /Product/:id/LocalStockAfter/…
 *
 * Extraction pattern (identical for every endpoint):
 * ```ts
 * const value: number = (result as StockResult[])[0]?.stock ?? 0;
 * ```
 */
interface StockResult {
  stock: number;
}

/**
 * @interface IncomePriceBreakdownRow
 * @description
 * One source intake batch contributing to a FIFO income price, as returned by
 * `GET /Product/:id/IncomePriceBreakdown/...` (`dbo.ufn_getprodincomeprice_breakdown_iTVF`).
 * `SourcePrice`/`DOCDATE` are the batch's own document; `ConvertedPrice`/`LineValue` are in
 * the requested currency. `IsFallback = true` means no on-hand batch matched at all — the
 * single row shown is the most-recent intake price applied to the full requested quantity,
 * not a real FIFO blend (see the SQL function's own header comment for when this fires).
 */
export interface IncomePriceBreakdownRow {
  DOCID: number;
  DOCNUMBER: string;
  DOCDATE: Date;
  SourcePrice: number;
  ConvertedPrice: number;
  UnitsFromBatch: number;
  LineValue: number;
  IsFallback: boolean;
}

/**
 * @class SalesDataProcessor
 * @description Utility class providing static helpers for transforming raw sales API data.
 */
class SalesDataProcessor {
  /**
   * Parses a raw API response object into a strongly-typed {@link ProductTransaction}.
   * Trims string fields and coerces numeric fields.
   *
   * @param {any} raw - Raw object from the API response.
   * @returns {ProductTransaction} Typed and sanitised transaction record.
   */
  static parseLineItem(raw: any): ProductTransaction {
    return {
      PRODCODE: raw.PRODCODE?.trim() || '',
      PRODID: Number(raw.PRODID),
      DOCITEMPRODNAME: raw.DOCITEMPRODNAME?.trim() || '',
      DOCITEMPRICE: Number(raw.DOCITEMPRICE),
      DOCITEMVPRICE: Number(raw.DOCITEMVPRICE),
      ORDERED: Number(raw.ORDERED),
      SHIPPED: Number(raw.SHIPPED),
      PGROUPNAME: raw.PGROUPNAME?.trim() || '',
      ProductCategory: raw.ProductCategory?.trim() || '',
      INVOICE: raw.INVOICE?.trim() || '',
      INVOICEID: Number(raw.INVOICEID),
      DOCDATE: new Date(raw.DOCDATE),
      DOCITEMID: Number(raw.DOCITEMID),
      CUSTOMER_NICKNAME: raw.CUSTOMER_NICKNAME?.trim() || '',
      CUSTOMER: raw.CUSTOMER?.trim() || '',
      CUSTOMERID: Number(raw.CUSTOMERID),
      SELLER: raw.SELLER?.trim() || '',
      AUTHOR: raw.AUTHOR?.trim() || '',
      DOCMANCLIID: Number(raw.DOCMANCLIID)
    };
  }
}

export {
  ProductTransaction,
  SalesDataProcessor,
  StockResult
};


/**
 * REST client for all product-related API operations. Covers products, product
 * groups, price variables, stock levels, vendor records, parts (BOM),
 * customs codes, product booking, turnover sheets, and the batch stock+price
 * endpoint used by the product list.
 *
 * Routes covered: `/Product`, `/Products`, `/ProdGroup`, `/PriceVariables`.
 *
 * Every request includes Webyto connection headers plus three extra
 * product-specific headers (`Webyto-Params-VAT`, `Webyto-Params-MainCurr`,
 * `Webyto-Params-BalCurr`) resolved from GetglobalsettingsService.
 *
 * @remarks
 * All methods apply `retry(1)` on network failure. Errors are surfaced via
 * `handleError`, which shows a browser `alert` (legacy pattern flagged for
 * Phase 1 replacement).
 */
@Injectable({
  providedIn: 'root'
})
export class ProductApiService {

  /** Base API URL resolved at runtime from global settings. */
  apiURL: string = '';

  /**
   * Creates an instance of `ProductApiService`.
   *
   * @param {HttpClient} http - Angular HTTP client.
   * @param {GetglobalsettingsService} globalsettingsservice - Global settings provider.
   */
  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /**
   * Builds a fully-qualified API URL by prepending the configured API server base URL.
   *
   * @param {string} url - Relative URL path (e.g. `'/Product/123'`).
   * @returns {string} Absolute API URL.
   */
  private _getApiUrl(url: string = ''): string {
    let _api: string = this.globalsettingsservice.getApiServerUrl();
    return _api + url;
  }

  /**
   * Fetches a single product by its internal ID.
   *
   * @param {number} id - Internal product ID (`PRODID`).
   * @param {Date} [dt=new Date()] - Reference date for price/stock calculation.
   * @param {number} [bc=0] - Optional balance currency ID (e.g. USD). `0` uses the default.
   * @param {number} [mc=0] - Optional main currency ID. `0` uses the default.
   * @returns {Observable<Product[]>} Observable emitting a single-element product array.
   */
  getProductById(id: number, dt: Date = new Date(), bc: number = 0, mc: number = 0): Observable<Product[]> {
    return this.http.get<Product[]>(
      this._getApiUrl('/Product/' + id + '?_bc=' + bc + '&_mc=' + mc + '&_fromDate=' + (dt ? new Date(dt) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Gets the earliest (least) date on which a product had stock in a given scope.
   * This is the wrong description - function returns the transaction date of the earliest document affecting stock, not the date of actual stock presence.
   * It is used to determine how far back in time we need to look for transactions when calculating current stock levels.
   *
   * @param {number} prodId - Internal product ID (`PRODID`).
   * @param {Date} [topDate=new Date()] - Upper bound date; results are not later than this date.
   * @param {number} [compId=0] - Company ID to search in. `0` searches all companies.
   * @param {number} [depId=0] - Department ID. `0` searches all departments.
   * @returns {Observable<any[]>} Observable emitting the least-date result set.
   */
  getProductLeastDate(prodId: number, topDate: Date = new Date(), compId: number = 0, depId: number = 0): Observable<any[]> {
    return this.http.get<any[]>(
      this._getApiUrl('/Product/' + prodId + '/LeastDate/' + compId + '/' + depId + '?_topDate=' + (topDate ? new Date(topDate) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Fetches a paginated, filtered, and sorted list of products belonging to a product group
   * (exclusive – only direct members, no sub-groups).
   *
   * @param {number} id - Product group ID.
   * @param {ProductListFetchParams} options - Pagination, sorting, and filter options.
   * @returns {Observable<Product[]>} Observable emitting the matching product list.
   */
  getProductsByGroupExFull(id: number, options: ProductListFetchParams): Observable<Product[]> {
    let queryString = '?'
      + (options.bc ? ('&_bc=' + options.bc) : '')
      + (options.mc ? ('&_mc=' + options.mc) : '')
      + (options.fd ? ('&_fromDate=' + new Date(options.fd).toISOString()) : '')
      + (options.st ? ('&_start=' + options.st) : '')
      + (options.ft ? ('&_end=' + (options.st + options.ft)) : '')
      + ((options.sp && options.sp.sf) ? ('&_sf=' + encodeURIComponent(options.sp.sf)) : '')
      + ((options.sp && options.sp.so) ? ('&_so=' + encodeURIComponent(options.sp.so)) : '')
      + (options.fs ? ('&_flt=' + encodeURIComponent(JSON.stringify(options.fs))) : '')
      + (options.stock1 ? ('&_stock1=' + (options.stock1)) : '')
      + (options.stock2 ? ('&_stock2=' + (options.stock2)) : '')
      + (options.stock3 ? ('&_stock3=' + (options.stock3)) : '')
      ;

    return this.http.get<Product[]>(
      this._getApiUrl('/Product/GroupExFull/' + id + queryString),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Fetches a paginated, filtered, and sorted list of products belonging to a product group
   * (exclusive – only direct members, no sub-groups).
   *
   * @param {number} id - Product group ID.
   * @param {ProductListFetchParams} options - Pagination, sorting, and filter options.
   * @returns {Observable<Product[]>} Observable emitting the matching product list.
   */
  getProductsByGroupEx(id: number, options: ProductListFetchParams): Observable<Product[]> {
    let queryString = '?'
      + (options.bc ? ('&_bc=' + options.bc) : '')
      + (options.mc ? ('&_mc=' + options.mc) : '')
      + (options.fd ? ('&_fromDate=' + new Date(options.fd).toISOString()) : '')
      + (options.st ? ('&_start=' + options.st) : '')
      + (options.ft ? ('&_end=' + (options.st + options.ft)) : '')
      + ((options.sp && options.sp.sf) ? ('&_sf=' + encodeURIComponent(options.sp.sf)) : '')
      + ((options.sp && options.sp.so) ? ('&_so=' + encodeURIComponent(options.sp.so)) : '')
      + (options.fs ? ('&_flt=' + encodeURIComponent(JSON.stringify(options.fs))) : '');

    return this.http.get<Product[]>(
      this._getApiUrl('/Product/GroupEx/' + id + queryString),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Fetches a paginated, filtered, and sorted list of products belonging to a product group
   * (inclusive – includes products from all descendant sub-groups).
   *
   * @param {number} id - Product group ID.
   * @param {ProductListFetchParams} options - Pagination, sorting, and filter options.
   * @returns {Observable<Product[]>} Observable emitting the matching product list.
   */
  getProductsByGroupIn(id: number, options: ProductListFetchParams): Observable<Product[]> {
    let queryString = '?'
      + (options.bc ? ('&_bc=' + options.bc) : '')
      + (options.mc ? ('&_mc=' + options.mc) : '')
      + (options.fd ? ('&_fromDate=' + new Date(options.fd).toISOString()) : '')
      + (options.st ? ('&_start=' + options.st) : '')
      + (options.ft ? ('&_end=' + (options.st + options.ft)) : '')
      + ((options.sp && options.sp.sf) ? ('&_sf=' + encodeURIComponent(options.sp.sf)) : '')
      + ((options.sp && options.sp.so) ? ('&_so=' + encodeURIComponent(options.sp.so)) : '')
      + (options.fs ? ('&_flt=' + encodeURIComponent(JSON.stringify(options.fs))) : '');

    return this.http.get<Product[]>(
      this._getApiUrl('/Product/GroupIn/' + id + queryString),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Performs a simple full-text search for products by name or description.
   *
   * @param {string} str - Search string.
   * @param {boolean} old - Whether to include discontinued/old products.
   * @param {boolean} com - Whether to include composite/kit products.
   * @returns {Observable<Product[]>} Observable emitting matching products.
   */
  getProductSimpleSearch(str: string, old: boolean, com: boolean): Observable<Product[]> {
    let queryString = '?_search=' + encodeURIComponent(str)
      + ('&o=' + old)
      + ('&c=' + com);

    return this.http.get<Product[]>(
      this._getApiUrl('/Product/SimpleSearch/' + queryString),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Finds products by their serial number string.
   *
   * @param {string} str - Serial number search string.
   * @param {boolean} old - Whether to include discontinued/old products.
   * @param {boolean} com - Whether to include composite/kit products.
   * @returns {Observable<any[]>} Observable emitting products matched by serial number.
   */
  getProductBySerialStr(str: string, old: boolean, com: boolean): Observable<any[]> {
    let queryString = '?_search=' + encodeURIComponent(str)
      + ('&o=' + old)
      + ('&c=' + com);

    return this.http.get<any[]>(
      this._getApiUrl('/Product/BySerialStr/' + queryString),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Performs a fast (indexed/typeahead) product search by name or description.
   *
   * @param {string} str - Search string.
   * @param {boolean} old - Whether to include discontinued/old products.
   * @param {boolean} com - Whether to include composite/kit products.
   * @returns {Observable<Product[]>} Observable emitting matching products.
   */
  getProductFastSearch(str: string, old: boolean, com: boolean): Observable<Product[]> {
    let queryString = '?_search=' + encodeURIComponent(str)
      + ('&o=' + old)
      + ('&c=' + com);

    return this.http.get<Product[]>(
      this._getApiUrl('/Product/FastSearch/' + queryString),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all child/component products linked to a parent product.
   *
   * @param {number} prodid - Parent product ID (`PRODID`).
   * @returns {Observable<Product[]>} Observable emitting the list of child products.
   */
  getProductsByParentProduct(prodid: number): Observable<Product[]> {
    return this.http.get<Product[]>(
      this._getApiUrl('/Product/ByParentProduct/' + prodid),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Finds products by their product code/SKU.
   *
   * @param {string} str - Product code to search for.
   * @param {boolean} old - Whether to include discontinued/old products.
   * @param {boolean} com - Whether to include composite/kit products.
   * @param {boolean} [part=false] - Whether to perform a partial (contains) match.
   * @returns {Observable<Product[]>} Observable emitting matching products.
   */
  getProductByCode(str: string, old: boolean, com: boolean, part: boolean = false): Observable<Product[]> {
    let queryString = '?prodcode=' + encodeURIComponent(str)
      + ('&o=' + old)
      + ('&c=' + com)
      + ('&p=' + part);

    return this.http.get<Product[]>(
      this._getApiUrl('/Product/ByCode/' + queryString),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Updates the main information fields of an existing product (HTTP PUT).
   *
   * @param {Product} prod - Product object containing updated fields. `PRODID` is used as the key.
   * @returns {Observable<number>} Observable emitting the number of rows
   *   affected (Prompt 13-F, interim response-contract rule — see
   *   `docs/PROPOSAL_rest-contract-alignment.md` in the API repo: writes
   *   return a row count, not the entity; re-`GET` for a fresh read).
   */
  putProductMainInfo(prod: Product): Observable<number> {
    return this.http.put<number>(
      this._getApiUrl('/Product/' + prod.PRODID),
      prod,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a new product record (HTTP POST).
   *
   * @param {Product} prod - Product object to create.
   * @returns {Observable<{ PRODID: number }>} Observable emitting the newly assigned `PRODID`.
   */
  postNewProductInfo(prod: Product): Observable<{ 'PRODID': number }> {
    return this.http.post<{ 'PRODID': number }>(
      this._getApiUrl('/Product/' + prod.PRODID),
      prod,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a full copy of an existing product.
   *
   * @param {Product} prod - Source product to copy. `PRODID` identifies the original.
   * @returns {Observable<number>} Observable emitting the `PRODID` of the newly created product.
   */
  postCopyProductInfo(prod: Product): Observable<number> {
    return this.http.post<number>(
      this._getApiUrl('/Product/' + prod.PRODID + '/Copy'),
      prod,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all part/component records associated with a product.
   *
   * @param {number} id - Product ID (`PRODID`).
   * @returns {Observable<ProductParts[]>} Observable emitting the list of product parts.
   */
  getProdParts(id: number): Observable<ProductParts[]> {
    return this.http.get<ProductParts[]>(
      this._getApiUrl('/Product/' + id + '/Parts'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves a specific part record for a product by its part ID.
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} pid - Part record ID.
   * @returns {Observable<ProductParts[]>} Observable emitting the matching part record.
   */
  getProdPartById(id: number, pid: number): Observable<ProductParts[]> {
    return this.http.get<ProductParts[]>(
      this._getApiUrl('/Product/' + id + '/Parts/' + pid),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Deletes a specific part record from a product.
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} pid - Part record ID to delete.
   * @returns {Observable<number>} Observable emitting the number of affected rows.
   */
  deleteProdPartsById(id: number, pid: number): Observable<number> {
    return this.http.delete<number>(
      this._getApiUrl('/Product/' + id + '/Parts/' + pid),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Updates a specific part record for a product (HTTP PUT).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} pid - Part record ID to update.
   * @param {ProductParts} pp - Updated part data.
   * @returns {Observable<number>} Observable emitting the number of affected rows.
   */
  putProdPartsById(id: number, pid: number, pp: ProductParts): Observable<number> {
    return this.http.put<number>(
      this._getApiUrl('/Product/' + id + '/Parts/' + pid),
      pp,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a new part record for a product (HTTP POST).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {ProductParts} pp - Part data to create.
   * @returns {Observable<number>} Observable emitting the newly created part record ID.
   */
  postProdParts(id: number, pp: ProductParts): Observable<number> {
    return this.http.post<number>(
      this._getApiUrl('/Product/' + id + '/Parts'),
      pp,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all vendor records associated with a product.
   *
   * @param {number} id - Product ID (`PRODID`).
   * @returns {Observable<ProdVendors[]>} Observable emitting the list of product vendors.
   */
  getProdVendors(id: number): Observable<ProdVendors[]> {
    return this.http.get<ProdVendors[]>(
      this._getApiUrl('/Product/' + id + '/Vendors'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the price/purchase history for a specific vendor of a product.
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} vid - Vendor record ID (`VENDORSITEMID`).
   * @returns {Observable<any[]>} Observable emitting the vendor history records.
   */
  getProdVendorHistoryById(id: number, vid: number): Observable<any[]> {
    return this.http.get<any[]>(
      this._getApiUrl('/Product/' + id + '/Vendor/' + vid + '/History'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves a specific vendor record for a product.
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} vid - Vendor record ID (`VENDORSITEMID`).
   * @returns {Observable<ProdVendors[]>} Observable emitting the matching vendor record.
   */
  getProdVendorById(id: number, vid: number): Observable<ProdVendors[]> {
    return this.http.get<ProdVendors[]>(
      this._getApiUrl('/Product/' + id + '/Vendor/' + vid),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Updates a specific vendor record for a product (HTTP PUT).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} vid - Vendor record ID to update.
   * @param {ProdVendors} pv - Updated vendor data.
   * @returns {Observable<number>} Observable emitting the number of affected rows.
   */
  putProdVendorById(id: number, vid: number, pv: ProdVendors): Observable<number> {
    return this.http.put<number>(
      this._getApiUrl('/Product/' + id + '/Vendor/' + vid),
      pv,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a new vendor record for a product (HTTP POST).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {ProdVendors} pv - Vendor data to create.
   * @returns {Observable<{ VENDORSITEMID: number }>} Observable emitting the newly created vendor record ID.
   */
  postProdVendorById(id: number, pv: ProdVendors): Observable<{ 'VENDORSITEMID': number }> {
    return this.http.post<{ 'VENDORSITEMID': number }>(
      this._getApiUrl('/Product/' + id + '/Vendor'),
      pv,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Deletes a specific vendor record from a product.
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} vid - Vendor record ID (`VENDORSITEMID`) to delete.
   * @returns {Observable<number>} Observable emitting the number of affected rows.
   */
  deleteProdVendorByRowId(id: number, vid: number): Observable<number> {
    return this.http.delete<number>(
      this._getApiUrl('/Product/' + id + '/Vendor/' + vid),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the income (purchase) price for a product under specified conditions.
   * Endpoint: `GET /Product/:id/IncomePrice/:cm/:s1/:cr/:qnty/?_fromDate=<fd>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} s1 - Storage/department ID.
   * @param {number} cr - Currency ID.
   * @param {number} qnty - Quantity to price.
   * @param {Date} fd - Reference date.
   * @returns {Observable<{ PRODINCOMEPRICE: number }[]>} Observable emitting the income price result.
   */
  getProdIncomePriceById(id: number, cm: number, s1: number, cr: number, qnty: number, fd: Date): Observable<{ PRODINCOMEPRICE: number }[]> {
    return this.http.get<{ PRODINCOMEPRICE: number }[]>(
      this._getApiUrl('/Product/' + id + '/IncomePrice/' + cm + '/' + s1 + '/' + cr + '/' + qnty + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the per-batch FIFO breakdown behind a `getProdIncomePriceById` result — which
   * source intake documents, at what price/quantity, were combined to compute it. Same
   * parameters as `getProdIncomePriceById`; used by the income-price breakdown popup
   * (`IncomePriceBreakdownDialogComponent`) so a shown income price can be verified visually.
   * Endpoint: `GET /Product/:id/IncomePriceBreakdown/:cm/:s1/:cr/:qnty/?_fromDate=<fd>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} s1 - Storage/department ID.
   * @param {number} cr - Currency ID.
   * @param {number} qnty - Quantity to price.
   * @param {Date} fd - Reference date (the document's own date, not "now" — see the design
   *   doc, `docs/PROMPT_income-price-breakdown-popup.md`).
   * @returns {Observable<IncomePriceBreakdownRow[]>} One row per contributing batch; empty
   *   array is a normal result (no intake history to show, not an error).
   */
  getProdIncomePriceBreakdownById(id: number, cm: number, s1: number, cr: number, qnty: number, fd: Date): Observable<IncomePriceBreakdownRow[]> {
    return this.http.get<IncomePriceBreakdownRow[]>(
      this._getApiUrl('/Product/' + id + '/IncomePriceBreakdown/' + cm + '/' + s1 + '/' + cr + '/' + qnty + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves stock information for a product across multiple storage locations.
   * Endpoint: `GET /Product/:id/StockInfo/:cm/:cr/:s1/:s2/:s3/?_fromDate=<fd>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} cr - Currency ID.
   * @param {number} s1 - Primary storage/department ID.
   * @param {number} s2 - Secondary storage/department ID.
   * @param {number} s3 - Tertiary storage/department ID.
   * @param {Date} fd - Reference date.
   * @returns {Observable<ProductInfo[]>} Observable emitting the stock info records.
   */
  getProdStockInfoById(id: number, cm: number, cr: number, s1: number, s2: number, s3: number, fd: Date): Observable<ProductInfo[]> {
    return this.http.get<ProductInfo[]>(
      this._getApiUrl('/Product/' + id + '/StockInfo/' + cm + '/' + cr + '/' + s1 + '/' + s2 + '/' + s3 + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves pricing information for a product across multiple storage locations.
   * Endpoint: `GET /Product/:id/PriceInfo/:cm/:cr/:s1/:s2/:s3/?_fromDate=<fd>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} cr - Currency ID.
   * @param {number} s1 - Primary storage/department ID.
   * @param {number} s2 - Secondary storage/department ID.
   * @param {number} s3 - Tertiary storage/department ID.
   * @param {Date} fd - Reference date.
   * @returns {Observable<ProductInfo[]>} Observable emitting the price info records.
   */
  getProdPriceInfoById(id: number, cm: number, cr: number, s1: number, s2: number, s3: number, fd: Date): Observable<ProductInfo[]> {
    return this.http.get<ProductInfo[]>(
      this._getApiUrl('/Product/' + id + '/PriceInfo/' + cm + '/' + cr + '/' + s1 + '/' + s2 + '/' + s3 + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves combined stock and pricing information for a product.
   * Endpoint: `GET /Product/:id/Info/:cm/:cr/:s1/:s2/:s3/?_fromDate=<fd>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} cr - Currency ID.
   * @param {number} s1 - Primary storage/department ID.
   * @param {number} s2 - Secondary storage/department ID.
   * @param {number} s3 - Tertiary storage/department ID.
   * @param {Date} fd - Reference date.
   * @returns {Observable<ProductInfo[]>} Observable emitting the combined info records.
   */
  getProdInfoById(id: number, cm: number, cr: number, s1: number, s2: number, s3: number, fd: Date): Observable<ProductInfo[]> {
    return this.http.get<ProductInfo[]>(
      this._getApiUrl('/Product/' + id + '/Info/' + cm + '/' + cr + '/' + s1 + '/' + s2 + '/' + s3 + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Batch-retrieves stock, price, vendor name, group name, and currency for an array of
   * products in a single HTTP + SQL round-trip.
   *
   * Replaces the per-row `getProdInfoById` / `getClientById` / `getProdGroupById` chain
   * used in `list-product-array`. POST body is used instead of GET query params because
   * known callers (e.g. `create-price-list`) produce lists of 500–2 000 products.
   *
   * Body field names match `POST /Product/BatchInfo`:
   *   `prodIds`, `cm`, `mc`, `bc`, `s1`, `s2`, `s3`, `fromDate`.
   *
   * @param {number[]} ids       - Array of PRODID values (max 2 000).
   * @param {number}   cm        - Company ID.
   * @param {number}   mc        - Main currency ID (UAH price).
   * @param {number}   bc        - Balance currency ID (USD price).
   * @param {number}   s1        - Primary depot ID.
   * @param {number}   s2        - Import depot ID (0 if disabled).
   * @param {number}   s3        - Aux depot ID (0 if disabled).
   * @param {Date}     fromDate  - Reference date.
   * @returns {Observable<ProductBatchInfo[]>} One record per PRODID; unordered.
   */
  getProdInfoBatch(
    ids: number[], cm: number, mc: number, bc: number,
    s1: number, s2: number, s3: number, fromDate: Date
  ): Observable<ProductBatchInfo[]> {
    return this.http.post<ProductBatchInfo[]>(
      this._getApiUrl('/Product/BatchInfo'),
      { prodIds: ids, cm, mc, bc, s1, s2, s3, fromDate },
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves historical stock and demand statistics for a product.
   * Endpoint: `GET /Product/:id/Stat/:cm/:dp/:pr/:s1/:s2/:s3/?_fromDate=<fd>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department/period ID.
   * @param {number} pr - Price type or period range.
   * @param {number} s1 - Primary storage/department ID.
   * @param {number} s2 - Secondary storage/department ID.
   * @param {number} s3 - Tertiary storage/department ID.
   * @param {Date} fd - Reference date.
   * @returns {Observable<ProductStat[]>} Observable emitting the product statistics.
   */
  getProdStatById(id: number, cm: number, dp: number, pr: number, s1: number, s2: number, s3: number, fd: Date): Observable<ProductStat[]> {
    return this.http.get<ProductStat[]>(
      this._getApiUrl('/Product/' + id + '/Stat/' + cm + '/' + dp + '/' + pr + '/' + s1 + '/' + s2 + '/' + s3 + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the current stock level for a product across storage locations.
   * Endpoint: `GET /Product/:id/Stock/:cm/:s1/:s2/:s3/?_fromDate=<fd>`
   *
   * Response field: `stock` (previously `PRODSTOCK`).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} s1 - Primary storage/department ID.
   * @param {number} s2 - Secondary storage/department ID.
   * @param {number} s3 - Tertiary storage/department ID.
   * @param {Date} fd - Reference date.
   * @returns {Observable<StockResult[]>} Observable emitting a single-element array with the `stock` value.
   */
  getProdStockById(id: number, cm: number, s1: number, s2: number, s3: number, fd: Date): Observable<StockResult[]> {
    return this.http.get<StockResult[]>(
      this._getApiUrl('/Product/' + id + '/Stock/' + cm + '/' + s1 + '/' + s2 + '/' + s3 + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves a product group by its ID.
   *
   * Backend returns the row directly (not wrapped in an array) as of
   * Prompt 13-H #3 — `null` when `id` doesn't match any `PGROUPID`, not an
   * empty array. Callers no longer need to index `data[0]`.
   *
   * @param {number} id - Product group ID (`PGROUPID`).
   * @returns {Observable<ProductGroup>} Observable emitting the product group, or `null` if not found.
   */
  getProdGroupById(id: number): Observable<ProductGroup> {
    return this.http.get<ProductGroup>(
      this._getApiUrl('/ProdGroup/' + id),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Updates an existing product group (HTTP PUT).
   *
   * @param {ProductGroup} pg - Product group object with updated values. `PGROUPID` is used as the key.
   * @returns {Observable<number>} Observable emitting the number of rows
   *   affected (Prompt 13-F interim response-contract rule, see
   *   `putProductMainInfo`).
   */
  putProdGroupById(pg: ProductGroup): Observable<number> {
    return this.http.put<number>(
      this._getApiUrl('/ProdGroup/' + pg.PGROUPID),
      pg,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a new product group (HTTP POST).
   *
   * @param {ProductGroup} pg - Product group object to create.
   * @returns {Observable<ProductGroup>} Observable emitting the created product group.
   */
  postProdGroupById(pg: ProductGroup): Observable<ProductGroup> {
    return this.http.post<ProductGroup>(
      this._getApiUrl('/ProdGroup/' + pg.PGROUPID),
      pg,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Deletes a product group by its ID.
   *
   * @param {ProductGroup} pg - Product group object to delete. `PGROUPID` is used as the key.
   * @returns {Observable<number>} Observable emitting the number of rows
   *   affected (Prompt 13-F interim response-contract rule, see
   *   `putProductMainInfo`). No call sites in this app as of this writing —
   *   confirmed via a repo-wide grep.
   */
  deleteProdGroupById(pg: ProductGroup): Observable<number> {
    return this.http.delete<number>(
      this._getApiUrl('/ProdGroup/' + pg.PGROUPID),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the complete list of all product groups.
   *
   * @returns {Observable<ProductGroup[]>} Observable emitting all product groups.
   */
  getProdGroups(): Observable<ProductGroup[]> {
    return this.http.get<ProductGroup[]>(
      this._getApiUrl('/ProdGroup/'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Searches for product groups by name.
   *
   * @param {string} group - Search string for the group name.
   * @param {boolean} [strict=true] - Whether to use an exact (strict) match.
   * @returns {Observable<ProductGroup[]>} Observable emitting matching product groups.
   */
  searchProdGroups(group: string, strict: boolean = true): Observable<ProductGroup[]> {
    let queryString = '?_search=' + encodeURIComponent(group)
      + ('&strict=' + strict);

    return this.http.get<ProductGroup[]>(
      this._getApiUrl('/ProdGroup/search/' + queryString),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the direct child product groups for a given group.
   *
   * Unlike `getProdGroupById()`/`getProdGroupParentById()` (single-row
   * lookups by primary key/relationship), this is a genuine list — a group
   * can have zero, one, or many direct children — so the backend correctly
   * returns an array and no unwrap is applied server-side (Prompt 13-H #4;
   * see `PROMPT_13-H-13-I_response-type-fixes.md`). This method's type was
   * previously the wrong shape (`Observable<ProductGroup>`, singular) despite
   * that; had zero live callers, so it was a latent type lie, not a live bug.
   *
   * @param {number} id - Parent product group ID (`PGROUPID`).
   * @returns {Observable<ProductGroup[]>} Observable emitting the child product groups.
   */
  getProdGroupChildById(id: number): Observable<ProductGroup[]> {
    return this.http.get<ProductGroup[]>(
      this._getApiUrl('/ProdGroup/' + id + '/Child'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the parent product group(s) for a given group.
   *
   * @param {number} id - Child product group ID (`PGROUPID`).
   * @returns {Observable<ProductGroup>} Observable emitting the parent product group(s).
   */
  getProdGroupParentById(id: number): Observable<ProductGroup> {
    return this.http.get<ProductGroup>(
      this._getApiUrl('/ProdGroup/' + id + '/Parent'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves extended booking status (reservations and orders) for a product.
   * Endpoint: `GET /Product/:id/BookingEx/:cm/:md/?_fromDate=<fd>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} md - Mode/department ID.
   * @param {Date} fd - Reference date.
   * @returns {Observable<ProductBooking[]>} Observable emitting the booking records.
   */
  getProductBookingEx(id: number, cm: number, md: number, fd: Date): Observable<ProductBooking[]> {
    return this.http.get<ProductBooking[]>(
      this._getApiUrl('/Product/' + id + '/BookingEx/' + cm + '/' + md + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Fetches the first segment of a product's turnover sheet (income transactions).
   * Designed to be called in parallel with `getProductTurnoverSheet2` and `getProductTurnoverSheet3`
   * via `merge()` to improve performance.
   * Endpoint: `GET /Product/:id/TurnoverSheet1/:cm/:dp/:cr/?_fromDate=<fd>&_toDate=<td>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {number} cr - Currency ID.
   * @param {Date} fd - Start date of the turnover period.
   * @param {Date} td - End date of the turnover period.
   * @returns {Observable<TurnoverRow[]>} Observable emitting turnover rows for segment 1.
   */
  getProductTurnoverSheet1(id: number, cm: number, dp: number, cr: number, fd: Date, td: Date): Observable<TurnoverRow[]> {
    return this.http.get<TurnoverRow[]>(
      this._getApiUrl('/Product/' + id + '/TurnoverSheet1/' + cm + '/' + dp + '/' + cr + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString() + '&_toDate=' + (td ? new Date(td) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Fetches the second segment of a product's turnover sheet (outcome/sales transactions).
   * Designed to be called in parallel with `getProductTurnoverSheet1` and `getProductTurnoverSheet3`.
   * Endpoint: `GET /Product/:id/TurnoverSheet2/:cm/:dp/:cr/?_fromDate=<fd>&_toDate=<td>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {number} cr - Currency ID.
   * @param {Date} fd - Start date of the turnover period.
   * @param {Date} td - End date of the turnover period.
   * @returns {Observable<TurnoverRow[]>} Observable emitting turnover rows for segment 2.
   */
  getProductTurnoverSheet2(id: number, cm: number, dp: number, cr: number, fd: Date, td: Date): Observable<TurnoverRow[]> {
    return this.http.get<TurnoverRow[]>(
      this._getApiUrl('/Product/' + id + '/TurnoverSheet2/' + cm + '/' + dp + '/' + cr + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString() + '&_toDate=' + (td ? new Date(td) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Fetches the third segment of a product's turnover sheet (adjustments/corrections).
   * Designed to be called in parallel with `getProductTurnoverSheet1` and `getProductTurnoverSheet2`.
   * Endpoint: `GET /Product/:id/TurnoverSheet3/:cm/:dp/:cr/?_fromDate=<fd>&_toDate=<td>`
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {number} cr - Currency ID.
   * @param {Date} fd - Start date of the turnover period.
   * @param {Date} td - End date of the turnover period.
   * @returns {Observable<TurnoverRow[]>} Observable emitting turnover rows for segment 3.
   */
  getProductTurnoverSheet3(id: number, cm: number, dp: number, cr: number, fd: Date, td: Date): Observable<TurnoverRow[]> {
    return this.http.get<TurnoverRow[]>(
      this._getApiUrl('/Product/' + id + '/TurnoverSheet3/' + cm + '/' + dp + '/' + cr + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString() + '&_toDate=' + (td ? new Date(td) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the stock level of a product part before a given date.
   * Endpoint: `GET /Product/:id/PartStockBefore/:cm/:dp/?_fromDate=<fd>`
   *
   * Response field: `stock` (previously `STOCKBE`).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {Date} fd - Reference date (exclusive upper bound).
   * @returns {Observable<StockResult[]>} Observable emitting a single-element array with the `stock` value.
   */
  getProductPartStockBefore(id: number, cm: number, dp: number, fd: Date): Observable<StockResult[]> {
    return this.http.get<StockResult[]>(
      this._getApiUrl('/Product/' + id + '/PartStockBefore/' + cm + '/' + dp + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the stock level of a product part after a given date.
   * Endpoint: `GET /Product/:id/PartStockAfter/:cm/:dp/?_fromDate=<fd>`
   *
   * Response field: `stock` (previously `STOCKAF`).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {Date} fd - Reference date (inclusive lower bound).
   * @returns {Observable<StockResult[]>} Observable emitting a single-element array with the `stock` value.
   */
  getProductPartStockAfter(id: number, cm: number, dp: number, fd: Date): Observable<StockResult[]> {
    return this.http.get<StockResult[]>(
      this._getApiUrl('/Product/' + id + '/PartStockAfter/' + cm + '/' + dp + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the stock level of a product before a given date.
   * Endpoint: `GET /Product/:id/StockBefore/:cm/:dp/?_fromDate=<fd>`
   *
   * Response field: `stock` (previously `STOCKBE`).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {Date} fd - Reference date (exclusive upper bound).
   * @returns {Observable<StockResult[]>} Observable emitting a single-element array with the `stock` value.
   */
  getProductStockBefore(id: number, cm: number, dp: number, fd: Date): Observable<StockResult[]> {
    return this.http.get<StockResult[]>(
      this._getApiUrl('/Product/' + id + '/StockBefore/' + cm + '/' + dp + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the stock level of a product after a given date.
   * Endpoint: `GET /Product/:id/StockAfter/:cm/:dp/?_fromDate=<fd>`
   *
   * Response field: `stock` (previously `STOCKAF`).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {Date} fd - Reference date (inclusive lower bound).
   * @returns {Observable<StockResult[]>} Observable emitting a single-element array with the `stock` value.
   */
  getProductStockAfter(id: number, cm: number, dp: number, fd: Date): Observable<StockResult[]> {
    return this.http.get<StockResult[]>(
      this._getApiUrl('/Product/' + id + '/StockAfter/' + cm + '/' + dp + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the local (warehouse-specific) stock level of a product before a given date.
   * Endpoint: `GET /Product/:id/LocalStockBefore/:cm/:dp/:lc/?_fromDate=<fd>`
   *
   * Response field: `stock` (previously `LCSTOCKBE`).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {number} lc - Local warehouse/location ID.
   * @param {Date} fd - Reference date (exclusive upper bound).
   * @returns {Observable<StockResult[]>} Observable emitting a single-element array with the `stock` value.
   */
  getProductLcStockBefore(id: number, cm: number, dp: number, lc: number, fd: Date): Observable<StockResult[]> {
    return this.http.get<StockResult[]>(
      this._getApiUrl('/Product/' + id + '/LocalStockBefore/' + cm + '/' + dp + '/' + lc + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the local (warehouse-specific) stock level of a product after a given date.
   * Endpoint: `GET /Product/:id/LocalStockAfter/:cm/:dp/:lc/?_fromDate=<fd>`
   *
   * Response field: `stock` (previously `LCSTOCKAF`).
   *
   * @param {number} id - Product ID (`PRODID`).
   * @param {number} cm - Company ID.
   * @param {number} dp - Department ID.
   * @param {number} lc - Local warehouse/location ID.
   * @param {Date} fd - Reference date (inclusive lower bound).
   * @returns {Observable<StockResult[]>} Observable emitting a single-element array with the `stock` value.
   */
  getProductLcStockAfter(id: number, cm: number, dp: number, lc: number, fd: Date): Observable<StockResult[]> {
    return this.http.get<StockResult[]>(
      this._getApiUrl('/Product/' + id + '/LocalStockAfter/' + cm + '/' + dp + '/' + lc + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the number of units of a product that were shipped on a particular document (invoice).
   * Sibling of `getDocItemsShippedByDoc` but uses a product-centric endpoint.
   * Endpoint: `GET /Product/:prodId/ShippedByDoc/:docId/?_fromDate=<fd>`
   *
   * @param {number} prodId - Product ID (`PRODID`).
   * @param {number} docId - Document (invoice) ID.
   * @param {Date} fd - Reference date.
   * @returns {Observable<{ DOCSHIPPEDITEMS: number }[]>} Observable emitting the shipped quantity.
   */
  getProductShippedByDoc(prodId: number, docId: number, fd: Date): Observable<{ DOCSHIPPEDITEMS: number }[]> {
    return this.http.get<{ DOCSHIPPEDITEMS: number }[]>(
      this._getApiUrl('/Product/' + prodId + '/ShippedByDoc/' + docId + '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all products currently in transit (ordered but not yet received).
   * Used for dashboard reporting.
   *
   * @param {number} depth - Lookback depth (e.g. number of days or levels to inspect).
   * @returns {Observable<any[]>} Observable emitting the in-transit product records.
   */
  getProductsInTransit(depth: number): Observable<any[]> {
    return this.http.get<any[]>(
      this._getApiUrl('/Products/InTransit/' + depth),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all products that have been sold since a given date.
   * Used for dashboard and sales reporting.
   *
   * @param {Date} dt - Start date for the sold-products query.
   * @returns {Observable<Product[]>} Observable emitting the sold product list.
   */
  getSoldProducts(dt: Date): Observable<Product[]> {
    return this.http.get<Product[]>(
      this._getApiUrl('/Products/Sold/?_fromDate=' + (dt ? new Date(dt) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all products that have been invoiced since a given date.
   * Used for dashboard and invoicing reports.
   *
   * @param {Date} dt - Start date for the invoiced-products query.
   * @returns {Observable<Product[]>} Observable emitting the invoiced product list.
   */
  getInvoicedProducts(dt: Date): Observable<Product[]> {
    return this.http.get<Product[]>(
      this._getApiUrl('/Products/Invoiced/?_fromDate=' + (dt ? new Date(dt) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all product parts/components that have been sold since a given date.
   *
   * @param {Date} dt - Start date for the sold-parts query.
   * @returns {Observable<Product[]>} Observable emitting the sold product parts list.
   */
  getSoldProductParts(dt: Date): Observable<Product[]> {
    return this.http.get<Product[]>(
      this._getApiUrl('/Products/SoldParts/?_fromDate=' + (dt ? new Date(dt) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all product parts/components sold since a given date, grouped by document.
   *
   * @param {Date} dt - Start date for the query.
   * @returns {Observable<any[]>} Observable emitting sold product parts grouped by document.
   */
  getSoldProductPartsByDoc(dt: Date): Observable<any[]> {
    return this.http.get<any[]>(
      this._getApiUrl('/Products/SoldPartsByDoc/?_fromDate=' + (dt ? new Date(dt) : new Date()).toISOString()),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the list of all available global product categories.
   *
   * @returns {Observable<string[]>} Observable emitting an array of global category name strings.
   */
  getProductGlobalCategogoryList(): Observable<string[]> {
    return this.http.get<string[]>(
      this._getApiUrl('/Product/ProductGlobalCategories/'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the assigned global product category for a specific product.
   *
   * @param {number} id - Product ID (`PRODID`).
   * @returns {Observable<string[]>} Observable emitting the category value(s) for the product.
   */
  getProductCategoryByID(id: number): Observable<string[]> {
    return this.http.get<string[]>(
      this._getApiUrl('/Product/' + id + '/ProductCategory'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Updates the global product category assignment for a product (HTTP PUT).
   *
   * @param {Product} prod - Product object whose `ProductCategory` field will be updated. `PRODID` is used as the key.
   * @returns {Observable<[{ PRODID: number; ProductCategory: string }]>} Observable emitting the updated assignment.
   */
  putProductCategoryByID(prod: Product): Observable<[{ 'PRODID': number, 'ProductCategory': string }]> {
    return this.http.put<[{ 'PRODID': number, 'ProductCategory': string }]>(
      this._getApiUrl('/Product/' + prod.PRODID + '/ProductCategory'),
      prod,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all Approved Customs Codes (ACTC) records for a product by its numeric ID.
   *
   * @param {number} prod - Product ID (`PRODID`).
   * @returns {Observable<ApprovedCustomsCodes[]>} Observable emitting the list of customs code records.
   */
  getActcListById(prod: number): Observable<ApprovedCustomsCodes[]> {
    return this.http.get<ApprovedCustomsCodes[]>(
      this._getApiUrl('/Product/' + prod + '/ApprovedCustomsCodes'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all Approved Customs Codes (ACTC) records for a product by its product object.
   *
   * @param {Product} prod - Product object. `PRODID` is used as the key.
   * @returns {Observable<ApprovedCustomsCodes[]>} Observable emitting the list of customs code records.
   */
  getActcList(prod: Product): Observable<ApprovedCustomsCodes[]> {
    return this.http.get<ApprovedCustomsCodes[]>(
      this._getApiUrl('/Product/' + prod.PRODID + '/ApprovedCustomsCodes'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Updates an Approved Customs Codes (ACTC) record for a product (HTTP PUT).
   *
   * @param {Product} prod - Product object containing the updated customs code data. `PRODID` is used as the key.
   * @returns {Observable<ApprovedCustomsCodes[]>} Observable emitting the updated customs code records.
   */
  putActcListItem(prod: Product): Observable<ApprovedCustomsCodes[]> {
    return this.http.put<ApprovedCustomsCodes[]>(
      this._getApiUrl('/Product/' + prod.PRODID + '/ApprovedCustomsCodes'),
      prod,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a new Approved Customs Codes (ACTC) record for a product (HTTP POST).
   *
   * @param {Product} prod - Product object containing the new customs code data.
   * @returns {Observable<ApprovedCustomsCodes[]>} Observable emitting the created customs code records.
   */
  postActcListItem(prod: Product): Observable<ApprovedCustomsCodes[]> {
    return this.http.post<ApprovedCustomsCodes[]>(
      this._getApiUrl('/Product/' + prod.PRODID + '/ApprovedCustomsCodes'),
      prod,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Deletes an Approved Customs Codes (ACTC) record for a product (HTTP DELETE).
   *
   * @param {Product} prod - Product object identifying the record to delete. `PRODID` is used as the key.
   * @returns {Observable<ApprovedCustomsCodes[]>} Observable emitting the remaining customs code records.
   */
  deleteActcListItem(prod: Product): Observable<ApprovedCustomsCodes[]> {
    return this.http.delete<ApprovedCustomsCodes[]>(
      this._getApiUrl('/Product/' + prod.PRODID + '/ApprovedCustomsCodes'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all price variables (formula parameters used in price calculations).
   *
   * @returns {Observable<PriceVariable[]>} Observable emitting the full list of price variables.
   */
  getPriceVariables(): Observable<PriceVariable[]> {
    return this.http.get<PriceVariable[]>(
      this._getApiUrl('/Variables/'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves a specific price variable by its ID.
   *
   * @param {number} id - Price variable ID.
   * @returns {Observable<PriceVariable[]>} Observable emitting the matching price variable record.
   */
  getPriceVariableById(id: number): Observable<PriceVariable[]> {
    return this.http.get<PriceVariable[]>(
      this._getApiUrl('/Variables/' + id),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Updates a price variable by its ID (HTTP PUT).
   *
   * @param {number} id - Price variable ID to update.
   * @param {PriceVariable} pv - Updated price variable data.
   * @returns {Observable<number>} Observable emitting the number of affected rows.
   */
  putPriceVariableById(id: number, pv: PriceVariable): Observable<number> {
    return this.http.put<number>(
      this._getApiUrl('/Variables/' + id),
      pv,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a new price variable (HTTP POST).
   *
   * @param {number} id - Price variable ID (used in the endpoint path).
   * @param {PriceVariable} pv - Price variable data to create.
   * @returns {Observable<number>} Observable emitting the newly created price variable ID.
   */
  postPriceVariableById(id: number, pv: PriceVariable): Observable<number> {
    return this.http.post<number>(
      this._getApiUrl('/Variables/' + id),
      pv,
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Deletes a price variable by its ID (HTTP DELETE).
   *
   * Deliberately does **not** pipe through `handleError` — the backend returns
   * `409` with a `{ error: string }` body naming the vendor(s) still referencing
   * this variable when the delete is blocked, and that message is user-facing
   * information the caller needs to show (via toast), not a generic alert.
   * Callers must inspect the raw `HttpErrorResponse` themselves.
   *
   * @param {number} id - Price variable ID to delete.
   * @returns {Observable<number>} Observable emitting the number of affected rows.
   */
  deletePriceVariableById(id: number): Observable<number> {
    return this.http.delete<number>(
      this._getApiUrl('/Variables/' + id),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1)
    );
  }

  /**
   * Searches for products that appear in documents matching the given filter criteria.
   *
   * Supported filter modes:
   * - `DOCTYPE` – filter by document type (array of numbers)
   * - `DOCFIELD` – filter by a specific document field value
   * - `PRODFIELD` – filter by a specific product field value
   *
   * `operMode` values for string fields: `'equal'` (default), `'contain'`, `'begin'`, `'end'`
   * `operMode` values for numeric fields: `'equal'`, `'greater'`, `'lesser'`
   * `operMode` values for date fields: `'equal'`, `'before'`, `'after'`, `'between'`
   *
   * @param {any[]} filter - JSON-serialisable filter array `[{ mode, value }]`.
   * @returns {Observable<(Product & { DOCSCOUNT?: number; TOTALQNTY?: number })[]>}
   *   Observable emitting products enriched with aggregated document counts and quantities.
   */
  findProductsInDocumentsByParameters(filter: any[]): Observable<Product[] & { DOCSCOUNT?: number; TOTALQNTY?: number }[]> {
    let parmStr: string = '';
    if (filter?.length) {
      parmStr = '?_filter=' + encodeURIComponent(JSON.stringify(filter));
    }
    return this.http.get<Product[] & { DOCSCOUNT?: number; TOTALQNTY?: number }[]>(
      this._getApiUrl('/Products/FromDocuments' + parmStr),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

/**
 * Retrieves stock level across multiple depots using the iTVF endpoint.
 * Single DB pass — 1.40x-3.18x faster than 3x scalar equivalent.
 *
 * Endpoint: GET /Product/:id/AllDepotsStock/:cm/:depots/?_fromDate=<fd>
 *
 * @param {number} id      - Product ID (PRODID).
 * @param {number} cm      - Company ID.
 * @param {string} depots  - Comma-separated depot IDs, e.g. '4258,4259,4260'.
 *                           Build from GlobalSettingsData: filter [ms,is,as] > 0,
 *                           join with ','.
 * @param {Date}   fd      - Reference date.
 * @returns {Observable<StockResult[]>} Single-element array with { stock: number }.
 *
 * @see getProdStockById  — legacy 3-param version, kept for backward compatibility.
 * @see REPORT_ufn_getstock_alldepots_iTVF.md
 */

  getProdAllDepotsStock(
    id: number,
    cm: number,
    depots: string,
    fd: Date
  ): Observable<StockResult[]> {
    return this.http.get<StockResult[]>(
      this._getApiUrl(
        '/Product/' + id + '/AllDepotsStock/' + cm + '/' + depots +
        '/?_fromDate=' + (fd ? new Date(fd) : new Date()).toISOString()
      ),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

/**
   * Retrieves sales transactions for products filtered by customer and optional document criteria.
   * Endpoint: `GET /Products/ByClientId?_filter=<encoded_filter>`
   *
   * @param {any[]} filter - JSON-serialisable filter array `[{ mode, value }]`.
   * @returns {Observable<ProductTransaction[]>} Observable emitting the matching sales transaction records.
   */
  findProductsTransactionsByCustomer(filter: any[]): Observable<ProductTransaction[]> {
    let parmStr: string = '';
    if (filter?.length) {
      parmStr = '?_filter=' + encodeURIComponent(JSON.stringify(filter));
    }
    return this.http.get<ProductTransaction[]>(
      this._getApiUrl('/Products/ByClientId' + parmStr),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves the serial number tracking history for a product across all income,
   * outcome, invoice, and manufacturing documents.
   *
   * @param {number} id - Internal product ID (`PRODID`).
   * @returns {Observable<ProductSerialHistory[]>} Observable emitting serial tracking records.
   */
  getProductSerials(id: number): Observable<ProductSerialHistory[]> {
    return this.http.get<ProductSerialHistory[]>(
      this._getApiUrl('/Product/' + id + '/Serials'),
      {
        headers: {
          ...this.globalsettingsservice.getStandardApiHeaders(),
          'Webyto-Params-VAT': this.globalsettingsservice.getVAT().toString(),
          'Webyto-Params-MainCurr': this.globalsettingsservice.getMainCurrencyId().toString(),
          'Webyto-Params-BalCurr': this.globalsettingsservice.getBalanceCurrencyId().toString(),
        }
      }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Central error handler for all HTTP operations in this service.
   * Displays an alert to the user and re-throws the error as an Observable.
   *
   * @param {any} error - The HTTP error response object.
   * @returns {Observable<never>} An Observable that immediately errors with the formatted message.
   */
  handleError(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(() => errorMessage);
  }
}
